import React, { useState } from 'react';
import { Facebook, Instagram, Send, Ticket } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import './Contact.css';
import { trackEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EVENTBRITE_URL = 'https://www.eventbrite.com/o/royal-bar-grill-120928056108';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 3000);
    } catch (err) {
      toast.error('Could not send your message. Please try again or call us at (458) 253-4058.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Questions? Reservations? Special events? We'd love to hear from you.</p>
        </div>

        <div className="contact-content">
          <div className="contact-form-wrapper">
            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>We've received your message and will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(458) 000-0000"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us about your inquiry..."
                  />
                </div>

                <button type="submit" className="btn-primary dark-button-animate" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>
              </form>
            )}
          </div>

          <div className="contact-social">
            <h3 className="social-title">Follow Us</h3>
            <p className="social-text">Stay updated with our latest events, menu items, and special offers.</p>
            <div className="social-links">
              <a href="https://www.facebook.com/share/1DoM8JfLno/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="social-link dark-hover" onClick={() => trackEvent('facebook_click', { link_location: 'contact' })}>
                <Facebook size={24} />
                <span>Facebook</span>
              </a>
              <a href="https://www.instagram.com/royalbaralbany?igsh=MWZxaGo0bnNiN21idQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="social-link dark-hover" onClick={() => trackEvent('instagram_click', { link_location: 'contact' })}>
                <Instagram size={24} />
                <span>Instagram</span>
              </a>
              <a href={EVENTBRITE_URL} target="_blank" rel="noopener noreferrer" className="social-link dark-hover" data-testid="eventbrite-social-link" onClick={() => trackEvent('eventbrite_click', { link_location: 'contact' })}>
                <Ticket size={24} />
                <span>Eventbrite</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;